<script lang="ts">
import { Users, Handshake, Ellipsis } from 'lucide-vue-next';

export default {
  name: 'TowerCard',
  components: {
    Users,
    Handshake,
    Ellipsis
  },
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { TowerCardData, TowerType } from '@/views/tower_dashboard/types/tower-dashboard';
import { DateTime } from 'luxon';
import { AppState } from '@/store';
import { useStore } from 'vuex';
import { IsUserABroker, IsUserAnAdmin } from '@/utilities/AuthUtils';

const props = defineProps<{
  cardData: TowerCardData,
  optionsOpen?: boolean,
  highlightTowerId?: string,
  highlightTowerBorderColor?: string,
}>();

const emit = defineEmits<{
  (e: 'click', towerId: string): void;
  (e: 'edit', towerId: string): void;
  (e: 'duplicate', towerId: string): void;
  (e: 'toggle-publish', towerId: string, published: boolean): void;
  (e: 'delete', towerCard: TowerCardData): void;
  (e: 'update:optionsOpen', optionsOpen: boolean): void;
  (e: 'convert:new-coverage', towerId: string): void;
  (e: 'convert:current-coverage', towerId: string, accountId: number): void;
}>();

const store = useStore<AppState>();

interface TowerTypeChip {
  bgColor: string;
  textColor: string;
  text: string;
}

const towerChips: Record<TowerType, TowerTypeChip> = {
  [TowerType.BoundCoverage]: { bgColor: 'tw:bg-blue-200', textColor: 'tw:text-blue-900', text: 'Bound Coverage' },
  [TowerType.Renewal]: { bgColor: 'tw:bg-red-200', textColor: 'tw:text-red-900', text: 'Renewal' },
  [TowerType.NewCoverage]: { bgColor: 'tw:bg-orange-200', textColor: 'tw:text-orange-900', text: 'New Coverage' },
  [TowerType.Scratch]: { bgColor: 'tw:bg-emerald-200', textColor: 'tw:text-emerald-900', text: 'Scratch' },
  [TowerType.MultiTower]: { bgColor: 'tw:bg-gray-200', textColor: 'tw:text-gray-900', text: 'MultiTower' },
};

const showOptions = ref(false);
const optionsMenu = ref<HTMLElement>();
const showContributors = ref<boolean>(false);


// ---------
// computed
// ---------
const versionLabel = computed(() => {
  if (props.cardData.versionLabel) {
    if (props.cardData.versionLabel.length > 60) {
      return props.cardData.versionLabel.substring(0, 60).trim() + '...';
    }
    return props.cardData.versionLabel;
  }
  return 'Initial Version';
});

const programName = computed(() => {
  let programName = '';
  if (props.cardData.program) {
    const program = props.cardData.program;
    
    if (program.type === 'Manual') {
      programName = `Manual ${program.year} ${program.name}`;
    } else {
      programName = program.name;
    }

    if (programName) {
      if (programName.length > 50) {
        return programName.substring(0, 50).trim() + '...';
      }
    }
    
    return programName;
  }

  return '';
});

const towerName = computed(() => {
  if (props.cardData.towerName) {
    if (props.cardData.towerName.length > 50) {
      return props.cardData.towerName.substring(0, 50).trim() + '...';
    }
    return props.cardData.towerName;
  }
  return '';
});

const accountName = computed(() => {
  if (props.cardData.accountName) {
    if (props.cardData.accountName.length > 50) {
      return props.cardData.accountName.substring(0, 50).trim() + '...';
    }

    return props.cardData.accountName;
  }
  return '';
});

const userEmail = computed(() => {
  if (props.cardData.user.userEmail) {
    if (props.cardData.user.userEmail.length > 50) {
      return props.cardData.user.userEmail.substring(0, 50).trim() + '...';
    }

    return props.cardData.user.userEmail;
  }
  return '';
});

const userName = computed(() => {
  if (props.cardData.user.name) {
    if (props.cardData.user.name.length > 50) {
      return props.cardData.user.name.substring(0, 50).trim() + '...';
    }

    return props.cardData.user.name;
  }
  return '';
});

const canDelete = computed(() => {
  if (props.cardData.published) {
    return false;
  }

  if (IsUserAnAdmin(store.state.user)) {
    return true;
  }

  if (IsUserABroker(store.state.user) && store.state.user?.user.id === props.cardData.user.id) {
    return true;
  }

  return false;
});

const canConvertToNewCoverageTower = computed(() => {
  return props.cardData.towerTypeId === TowerType.Scratch
});

const canConvertToBoundCoverageTower = computed(() => {
  return props.cardData.towerTypeId === TowerType.NewCoverage || props.cardData.towerTypeId === TowerType.Renewal
});

const updatedOn = computed(() => {
  if (props.cardData.updatedOn) {
    let date = DateTime.fromISO(props.cardData.updatedOn);

    if (date.startOf('day').equals(DateTime.now().startOf('day'))) {
      return date.toLocaleString(DateTime.TIME_SIMPLE);
    }

    return date.toLocaleString(DateTime.DATE_MED) + ' - ' + date.toLocaleString(DateTime.TIME_SIMPLE);
  }
  return '';
});

const highlightCard = computed(() => {
  return props.highlightTowerId === props.cardData.towerId && props.highlightTowerBorderColor !== undefined;
});

// ---------
// methods
// ---------
function handleEditTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  emit('edit', props.cardData.towerId);
}

function handleDuplicateTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  emit('duplicate', props.cardData.towerId);
}

function handleConvertToNewCoverageTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  emit('convert:new-coverage', props.cardData.towerId);
}

function handleConvertToBoundCoverageTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  console.log('convert to bound coverage', props.cardData);
  emit('convert:current-coverage', props.cardData.towerId, props.cardData.accountId ?? 0);
}

function handleDeleteTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  emit('delete', props.cardData);
}

function handlePublishTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  emit('toggle-publish', props.cardData.towerId, !props.cardData.published);
}

function toggleOptionsMenu(e: Event) {
  e.stopPropagation();
  showOptions.value = !showOptions.value;
  emit('update:optionsOpen', showOptions.value)

  // set focus on open for blur to work
  if (showOptions.value) {
    setTimeout(() => {
      optionsMenu.value?.focus();
    }, 50);
  }
}

function handleOptionsBlur(e: FocusEvent) {
  if (showOptions.value) {
    emit('update:optionsOpen', false);
    showOptions.value = false;
  }
}
</script>

<template>
  <div class="tw:group tw:flex group
         tw:pointer-events-auto tw:cursor-pointer tw:relative">
    <div
      @click="$emit('click', cardData.towerId)"
      class="tw:flex tw:flex-col tw:justify-between
        tw:w-80 tw:h-56
        tw:rounded-md
        tw:pointer-events-auto tw:cursor-pointer
        tw:px-2 tw:py-2
        tw:shadow-md tw:border tw:border-gray-300 tw:border-solid
        tw:bg-white
        tw:transition-all tw:duration-150
        tw:group-hover:shadow-none tw:group-hover:border-blue-200 tw:group-hover:bg-blue-50 tw:group
      tw:text-[#133044] tw:relative" :class="highlightCard ? `tw:border-${highlightTowerBorderColor}` : ''">
      
      <div class="tw:flex tw:justify-between">
        <div class="tw:w-full tw:flex tw:overflow-hidden tw:px-2">
          <p :title="cardData.versionLabel" class="tw:text-base tw:font-medium tw:grow tw:break-words">
            {{ versionLabel }}
          </p>
        </div>

        <div ref="optionsMenu" tabindex="0" @blur="handleOptionsBlur" @click="toggleOptionsMenu"
             class="tw:bg-white tw:flex tw:rounded-md tw:overflow-hidden tw:place-content-center tw:cursor-pointer
              tw:hover:bg-gray-100 tw:h-8 tw:w-10
              tw:z-10">
          
          <Ellipsis class="tw:text-gray-400 tw:m-auto tw:z-10" />

          <div v-if="showOptions" class="tw:absolute tw:right-2 tw:top-11">
            <ul class="tw:menu tw:bg-white tw:rounded-lg tw:border tw:border-solid tw:border-gray-200 tw:shadow-xs">
              <li>
                <a @click="handleEditTower" class="tw:text-black">Rename</a>
                <a @click="handleDuplicateTower" class="tw:text-black">Duplicate</a>
                <a v-if="canConvertToNewCoverageTower" @click="handleConvertToNewCoverageTower" class="tw:text-black">Associate To Client</a>
                <a v-if="canConvertToBoundCoverageTower" @click="handleConvertToBoundCoverageTower" class="tw:text-black">Associate To Program</a>
                <a @click="handlePublishTower" class="tw:text-black">{{ cardData.published ? 'Unpublish' : 'Publish to Clients'}}</a>
                <a v-if="canDelete" @click="handleDeleteTower" class="tw:text-primary">Delete</a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div
        class="tw:flex tw:mb-auto tw:rounded-lg tw:bg tw:h-6 tw:w-fit tw:px-3 tw:justify-center tw:align-center"
        :class="towerChips[cardData.towerTypeId as TowerType].bgColor + ' ' + towerChips[cardData.towerTypeId as TowerType].textColor">
           <span class="tw:my-auto">{{ towerChips[cardData.towerTypeId as TowerType].text
             }}</span>
      </div>

      <div class="tw:ml-2 tw:my-auto">
        <p class="  tw:text-sm tw:text-gray-600">
          {{ programName && programName !== '' ? programName : towerName }}</p>
        <p class="tw:text-xs  tw:font-bold">{{ accountName }}</p>
      </div>

      <div class="tw:flex tw:flex-row tw:w-full tw:gap-2 tw:mt-auto tw:text-xs tw:text-end tw:justify-between tw:px-1">
        <div v-if="cardData.published" class="tw:flex tw:justify-end tw:tooltip tw:tooltip-top tw:z-20" data-tip="Published to Clients">
          <Users class="tw:text-green-500 tw:self-end" :size="20"/>
        </div>
        
        <div v-if="props.cardData.contributors && props.cardData.contributors.length > 0" class="tw:relative tw:flex tw:justify-end">
          <Handshake
            :size="20"
            class="tw:text-gray-400 tw:self-end"
            @mouseover="showContributors = true"
            @mouseleave="showContributors = false"
          />
          <div v-show="showContributors"
            class="tw:absolute tw:text-start tw:whitespace-nowrap tw:border tw:border-gray-300 tw:bg-white
              tw:shadow-md tw:px-4 tw:py-2 tw:rounded-md tw:left-0 tw:top-8 tw:mt-1 tw:z-50">
            <p class="tw:text-sm tw:font-bold tw:mb-2">Contributors</p>
            <ul class="tw:list-none tw:m-0 tw:p-0">
              <li v-for="(contributor, index) in props.cardData.contributors.split(',')" :key="index" class="tw:text-sm">
                {{ contributor }}
              </li>
            </ul>
          </div>
        </div>

        <div class="tw:flex tw:flex-col tw:mr-0 tw:ml-auto">
          <p class="tw:mb-0 tw:mt-auto tw:break-all">{{ userName !== '' ? userName : userEmail }}</p>
          <p class="tw:mb-0 tw:font tw:text-gray-500">Updated {{ updatedOn }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
</style>
